import Image from "next/image";
import React from "react";
import { DirectPaths } from "@/config/routes";
import { cn } from "@/lib/utils";
import External from "@/app/assets/icons/external";
import ipoMegaphone from "@/app/assets/images/icm-3.0/ipo-megaphone.png";
import TrackedLink from "@/app/components/icm 3.0/shared/TrackedLink";

type IpoListing = {
  name: string;
  meta: string;
  priceRange?: string;
  statusLabel?: string;
  Logo: React.ComponentType<{ className?: string }>;
};

const SwiggyLogo = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 54 54" fill="none" aria-hidden>
    <rect width="54" height="54" rx="6" fill="#FF5200" />
    <path
      d="M27.27 12.825c-6.21 0-10.395 3.78-10.395 8.91 0 3.375 1.89 5.94 5.13 7.695l-1.215 4.32c-.135.54.405.945.81.675l4.995-2.97c.27 0 .54.135.81.135 6.21 0 10.53-3.915 10.53-9.18 0-5.67-4.32-9.585-10.665-9.585Zm.405 14.04c-3.915 0-6.48-2.295-6.48-5.535s2.565-5.535 6.48-5.535 6.48 2.295 6.48 5.535-2.565 5.535-6.48 5.535Z"
      fill="white"
    />
  </svg>
);

const NseLogo = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 54 54" fill="none" aria-hidden>
    <rect width="54" height="54" rx="6" fill="#F7F4FF" />
    <path d="M27 10.8 37.8 19.575 27 28.35 16.2 19.575 27 10.8Z" fill="#E11D48" />
    <path d="M37.8 19.575 43.2 27 32.4 35.775 27 28.35l10.8-8.775Z" fill="#F59E0B" />
    <path d="M27 28.35 32.4 35.775 21.6 43.875 16.2 36.45 27 28.35Z" fill="#7C3AED" />
    <path d="M16.2 19.575 27 28.35 16.2 36.45 10.8 27l5.4-7.425Z" fill="#F97316" />
  </svg>
);

const BoatLogo = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 54 54" fill="none" aria-hidden>
    <rect width="54" height="54" rx="6" fill="#111111" />
    <path
      d="M16.2 35.1c4.725-1.35 10.8-2.025 18.9-1.35-3.375 4.05-8.775 6.75-13.5 6.75-2.7 0-4.725-.675-5.4-2.025Z"
      fill="#E11D2E"
    />
    <path d="M24.3 13.5c8.1 5.4 12.15 12.15 13.5 18.9H24.3V13.5Z" fill="#E11D2E" />
  </svg>
);

const IPO_LISTINGS: IpoListing[] = [
  {
    name: "Swiggy",
    meta: "Consumer Tech • Closes in 2 Days",
    priceRange: "₹390–₹412",
    statusLabel: "LIVE NOW",
    Logo: SwiggyLogo,
  },
  {
    name: "NSE",
    meta: "Financial Services • Opens Tomorrow",
    priceRange: "₹1,650–₹1,720",
    Logo: NseLogo,
  },
  {
    name: "boAt",
    meta: "Consumer Electronics • Upcoming",
    statusLabel: "COMING SOON",
    Logo: BoatLogo,
  },
];

interface IpoCardProps {
  className?: string;
  ctaHref?: string;
}

/**
 * IPO product card — desktop 5882:65754; mobile collapsed under View More
 */
export function IpoCard({ className, ctaHref = DirectPaths.BROKING }: IpoCardProps) {
  return (
    <article
      className={cn(
        "relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#E6E6E6] bg-white p-6 md:p-[23px]",
        className
      )}
    >
      <div className="relative flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-6">
          <span className="inline-flex h-[30px] w-fit items-center justify-center rounded-lg border border-[#FDE7D7] bg-[#FEF5EE] px-3 py-1 text-sm font-medium leading-5 text-brand-primary-500">
            IPOs
          </span>
          <div className="flex flex-col gap-2 tracking-[0] md:gap-3">
            <h3 className="text-2xl font-bold leading-8 text-black md:whitespace-nowrap md:text-4xl md:leading-[44px]">
              Never miss the next big listing.
            </h3>
            <p className="max-w-[532px] text-base leading-5 text-[#767676] md:text-lg md:leading-6">
              Apply to IPOs before they list. Track, apply and invest companies
              before they hit the market.
            </p>
          </div>
        </div>

        <Image
          src={ipoMegaphone}
          alt=""
          width={104}
          height={96}
          className="hidden h-14 w-[60px] shrink-0 object-contain sm:block md:h-24 md:w-[104px]"
          aria-hidden
        />
      </div>

      <ul className="mt-6 flex w-full flex-col gap-4 md:mt-auto md:gap-5">
        {IPO_LISTINGS.map(({ name, meta, priceRange, statusLabel, Logo }) => (
          <li
            key={name}
            className="flex h-[82px] w-full items-center justify-between overflow-hidden rounded-lg border border-[#E6E6E6] bg-white px-3 md:px-[15px]"
          >
            <div className="flex min-w-0 items-center gap-3 md:gap-4">
              <Logo className="size-10 shrink-0 rounded-md border border-[#E6E6E6] md:size-[54px] md:rounded-[6px]" />
              <div className="flex min-w-0 flex-col gap-1 md:gap-2">
                <p className="truncate text-base font-semibold leading-6 text-black md:text-lg">
                  {name}
                </p>
                <p className="truncate text-sm leading-5 text-[#767676] md:text-base md:leading-[22px]">
                  {meta}
                </p>
              </div>
            </div>

            {(priceRange || statusLabel) && (
              <div className="flex shrink-0 flex-col items-end justify-center gap-1 text-right tracking-[0] md:gap-2">
                {priceRange ? (
                  <p className="text-base font-bold leading-6 text-black md:text-lg">
                    {priceRange}
                  </p>
                ) : null}
                {statusLabel ? (
                  <p className="text-sm font-normal leading-5 text-brand-primary-500 md:text-base md:leading-[22px]">
                    {statusLabel}
                  </p>
                ) : null}
              </div>
            )}
          </li>
        ))}
      </ul>

      <TrackedLink
        href={ctaHref}
        className="mt-5 inline-flex w-fit items-center gap-1 text-sm font-medium leading-5 text-ink-link underline"
        event="Home_Nav_Bar_Broking"
        cleverTapEvent="Navigation_Broking"
      >
        View IPOs
        <External className="size-4" aria-hidden />
      </TrackedLink>
    </article>
  );
}
